import { useEffect, useState } from "react";
import type { Bootstrap, Category, TypeConfig } from "../../shared/types";
import { api, type IncomeInput, type ManagedMember } from "../lib/api";
import { parseMoney, formatMoney } from "../lib/format";
import CategoryModal from "../components/CategoryModal";
import TypeModal from "../components/TypeModal";
import MemberModal from "../components/MemberModal";

export default function Settings({
  data,
  reload,
}: {
  data: Bootstrap | null;
  reload: () => Promise<void>;
}) {
  const [catModal, setCatModal] = useState(false);
  const [editCat, setEditCat] = useState<Category | null>(null);
  const [typeModal, setTypeModal] = useState(false);
  const [editType, setEditType] = useState<TypeConfig | null>(null);

  if (!data) return <div className="state">Loading…</div>;

  return (
    <div className="page">
      <h1 className="page__title">Settings</h1>
      <p className="page__lede">Your tracker name, salaries, categories and colours.</p>

      <TrackerName data={data} reload={reload} />

      <People reload={reload} />

      <Salaries data={data} reload={reload} />

      <section className="panel settings-panel">
        <div className="settings-panel__head">
          <h3 className="panel__title" style={{ margin: 0 }}>Categories</h3>
          <button className="btn btn--primary btn--sm" onClick={() => { setEditCat(null); setCatModal(true); }}>+ Add category</button>
        </div>
        <div className="cat-grid">
          {data.categories.map((c) => (
            <button className="cat-tile" key={c.id} onClick={() => { setEditCat(c); setCatModal(true); }}>
              <span className="chip__dot" style={{ background: c.colour }} />
              <span className="cat-tile__name">{c.name}</span>
              <span className="cat-tile__edit" aria-hidden="true">✎</span>
            </button>
          ))}
        </div>
      </section>

      <section className="panel settings-panel">
        <div className="settings-panel__head">
          <h3 className="panel__title" style={{ margin: 0 }}>Types</h3>
          <button className="btn btn--primary btn--sm" onClick={() => { setEditType(null); setTypeModal(true); }}>+ Add type</button>
        </div>
        <p className="panel__sub">Bills are one of these. Click a type to edit its label, description or colour.</p>
        {data.types.map((t) => (
          <button className="type-row type-row--edit" key={t.key} onClick={() => { setEditType(t); setTypeModal(true); }}>
            <span className="type-row__badge" style={{ background: t.colour + "22", color: t.colour }}>{t.label[0]}</span>
            <div className="type-row__main">
              <p className="type-row__label">{t.label}</p>
              <p className="type-row__desc">{t.description}</p>
            </div>
            <span className="cat-tile__edit" aria-hidden="true">✎</span>
          </button>
        ))}
      </section>

      {catModal && (
        <CategoryModal category={editCat} onClose={() => setCatModal(false)} onSaved={async () => { setCatModal(false); await reload(); }} />
      )}
      {typeModal && (
        <TypeModal type={editType} onClose={() => setTypeModal(false)} onSaved={async () => { setTypeModal(false); await reload(); }} />
      )}
    </div>
  );
}

function TrackerName({ data, reload }: { data: Bootstrap; reload: () => Promise<void> }) {
  const [name, setName] = useState(data.household.name);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!name.trim()) return setError("Give the tracker a name");
    setSaving(true);
    setError(null);
    try {
      await api.setTrackerName(name.trim());
      await reload();
      setSaved(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="panel settings-panel">
      <h3 className="panel__title">Tracker name</h3>
      <p className="panel__sub">Shown under “Expense Tracker” in the sidebar.</p>
      <div className="form">
        <div className="form__row">
          <label className="field">
            <span className="field__label">Name</span>
            <input className="input" value={name} onChange={(e) => { setName(e.target.value); setSaved(false); }} placeholder="Dittberner" maxLength={40} />
          </label>
        </div>
        {error && <p className="form__error">{error}</p>}
        <div className="form__actions">
          {saved && <span className="settings-saved">Saved ✓</span>}
          <button className="btn btn--primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save name"}</button>
        </div>
      </div>
    </section>
  );
}

function People({ reload }: { reload: () => Promise<void> }) {
  const [members, setMembers] = useState<ManagedMember[] | null>(null);
  const [modal, setModal] = useState(false);
  const [editMember, setEditMember] = useState<ManagedMember | null>(null);

  async function load() {
    const r = await api.listMembers();
    setMembers(r.members);
  }
  useEffect(() => {
    load();
  }, []);

  async function afterSave() {
    setModal(false);
    await load();
    await reload();
  }

  return (
    <section className="panel settings-panel">
      <div className="settings-panel__head">
        <h3 className="panel__title" style={{ margin: 0 }}>People &amp; logins</h3>
        <button className="btn btn--primary btn--sm" onClick={() => { setEditMember(null); setModal(true); }}>+ Add person</button>
      </div>
      <p className="panel__sub">Everyone who can sign in. Each person logs in with their own username and password.</p>

      <div className="people-list">
        {members == null ? (
          <p className="panel__sub">Loading…</p>
        ) : members.length === 0 ? (
          <p className="panel__sub">No one yet.</p>
        ) : (
          members.map((m) => (
            <button className="people-row" key={m.id} onClick={() => { setEditMember(m); setModal(true); }}>
              <span className="avatar" style={{ background: m.colour }}>{m.initial}</span>
              <span className="people-row__main">
                <span className="people-row__name">{m.name}</span>
                <span className="people-row__sub">
                  {m.hasLogin ? `@${m.username}` : "No login yet"}
                </span>
              </span>
              <span className="cat-tile__edit" aria-hidden="true">✎</span>
            </button>
          ))
        )}
      </div>

      {modal && <MemberModal member={editMember} onClose={() => setModal(false)} onSaved={afterSave} />}
    </section>
  );
}

function Salaries({ data, reload }: { data: Bootstrap; reload: () => Promise<void> }) {
  const initial = data.household.members.map((m) => {
    const e = data.income.find((i) => i.memberId === m.id);
    return { memberId: m.id, name: m.name, amount: e ? (e.amountMinor / 100).toFixed(2) : "", payDay: e?.payDay != null ? String(e.payDay) : "" };
  });
  const [rows, setRows] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const total = rows.reduce((t, r) => t + (parseMoney(r.amount) ?? 0), 0);

  function update(id: number, field: "amount" | "payDay", value: string) {
    setRows((rs) => rs.map((r) => (r.memberId === id ? { ...r, [field]: value } : r)));
    setSaved(false);
  }

  async function save() {
    const entries: IncomeInput[] = [];
    for (const r of rows) {
      const pd = r.payDay ? Number(r.payDay) : null;
      if (pd != null && (pd < 1 || pd > 31)) return setError("Pay day must be 1–31");
      entries.push({ memberId: r.memberId, amountMinor: parseMoney(r.amount) ?? 0, payDay: pd });
    }
    setSaving(true);
    setError(null);
    try {
      await api.saveIncome(entries);
      await reload();
      setSaved(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="panel settings-panel">
      <h3 className="panel__title">Expected salaries</h3>
      <p className="panel__sub">Monthly income per person, and the day it typically lands.</p>
      <div className="form">
        {rows.map((r) => (
          <div className="form__row" key={r.memberId}>
            <label className="field">
              <span className="field__label">{r.name} — monthly (£)</span>
              <input className="input" inputMode="decimal" value={r.amount} onChange={(e) => update(r.memberId, "amount", e.target.value)} placeholder="3293.38" />
            </label>
            <label className="field" style={{ maxWidth: 120 }}>
              <span className="field__label">Pay day</span>
              <input className="input" inputMode="numeric" value={r.payDay} onChange={(e) => update(r.memberId, "payDay", e.target.value)} placeholder="25" />
            </label>
          </div>
        ))}
        <div className="combined-row">
          <span>Combined monthly income</span>
          <strong>{formatMoney(total)}</strong>
        </div>
        {error && <p className="form__error">{error}</p>}
        <div className="form__actions">
          {saved && <span className="settings-saved">Saved ✓</span>}
          <button className="btn btn--primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save salaries"}</button>
        </div>
      </div>
    </section>
  );
}
